import { z } from 'zod';
import { zodTextFormat } from 'openai/helpers/zod';
import {
  categoryToVistaMapping,
  vistaToRubricMapping,
} from 'constants/evaluation';

const API_BASE_URL =
  import.meta.env.VITE_CMS_BASEURL || 'https://content.srx.id';
const CMS_API_TOKEN = import.meta.env.VITE_CMS_API_TOKEN;

// Zod schema for AI guidance response
const GuidanceSchema = z.object({
  suggestions: z
    .array(z.string())
    .min(2)
    .max(3)
    .describe('2-3 concise suggestions for the field'),
});

interface AiResponse {
  fieldName: string;
  category: string;
  guidance: string;
  suggestions?: string[];
  error?: boolean;
  parseError?: boolean;
}
/**
 * Get AI guidance for a specific field based on the current content and rubric
 */
export const getAIGuidance = async (
  fieldName: string,
  fieldLabel: string,
  currentContent: string,
  category: string,
  rubricData: any,
  recaptchaToken?: string
): Promise<AiResponse> => {
  try {
    // Extract aspect information from rubric data
    const aspects = rubricData?.aspect_list || [];
    const aspectInfo = aspects
      .map(aspect => {
        const aspectData = aspect.aspect || aspect;
        return {
          name: aspectData.name,
          description: aspectData.description,
          weight: aspect.weight || aspectData.weight,
        };
      })
      .filter(a => a.name && a.description);

    // Create field-specific guidance prompt
    const guidancePrompt = `You are an AI assistant helping users fill out an award submission form.

Context:
- Award Category: ${category}
- Field: "${fieldLabel}" (${fieldName})
- Current content: "${currentContent || 'Empty'}"
- Scoring aspects: ${aspectInfo.map(a => `${a.name} (${a.weight}%)`).join(', ')}

Provide exactly 2-3 concise but general directions (max 12 words each) specifically for the "${fieldLabel}" field. Vaguely provide information that aligns with ${aspectInfo.map(a => a.name).join(', ')} aspects.
Make sure the suggestions doesn't become a cheatcode

Return your response as JSON with a "suggestions" array containing the suggestions.`;

    // Using OpenAI's zodTextFormat helper for consistent structured outputs

    const response = await fetch(`${API_BASE_URL}/api/vista-ai/responses`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${CMS_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        input: guidancePrompt,
        text: {
          format: zodTextFormat(GuidanceSchema, 'guidance_response'),
        },
        recaptchaToken: recaptchaToken || 'dev-bypass',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `AI guidance failed: ${response.statusText} - ${errorText}`
      );
    }

    const aiResponse = await response.json();
    const guidanceText = aiResponse.output?.[0]?.content?.[0]?.text || '';

    // Parse and validate the structured response
    try {
      const parsedGuidance = JSON.parse(guidanceText);
      const validatedGuidance = GuidanceSchema.parse(parsedGuidance);

      return {
        guidance: validatedGuidance.suggestions.join('\n'),
        suggestions: validatedGuidance.suggestions,
        fieldName,
        category,
      };
    } catch {
      // Fallback to simple text parsing for backward compatibility
      return {
        guidance: guidanceText.trim(),
        fieldName,
        category,
        parseError: true,
      };
    }
  } catch {
    return {
      guidance:
        "Consider including relevant details that showcase your work's impact and innovation.",
      fieldName,
      category,
      error: true,
    };
  }
};

/**
 * Get rubric data for a specific category
 */
export const getRubricForCategory = async category => {
  try {
    const vistaCategoryName = categoryToVistaMapping(category);
    const expectedRubricName = vistaToRubricMapping(vistaCategoryName);

    if (!expectedRubricName) {
      return null;
    }

    // Fetch all rubrics and find the matching one
    const response = await fetch(`${API_BASE_URL}/api/vista-rubrics`, {
      headers: {
        Authorization: `Bearer ${CMS_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch rubrics: ${response.statusText}`);
    }

    const rubricsData = await response.json();
    const rubrics = rubricsData.data || rubricsData;

    const categoryRubric = rubrics.find(
      rubric => rubric.name === expectedRubricName
    );

    return categoryRubric;
  } catch {
    return null;
  }
};
